import {
  ScannerStatusEnum,
  RunStatusEnum,
  getRunStatusByKey,
  getScannerStatusByKey,
  ASSET_TYPE_LABELS
} from '../types/commonTypes';
import { getAgentProviders } from '../components/ScannerConfigurationForm/AuthenticationFields/agentProvidersService';

/**
 * Escapes HTML special characters to prevent XSS attacks
 * Use this function when displaying user-provided content in HTML contexts
 *
 * @param {string} str - The string to escape
 * @returns {string} The escaped string safe for HTML insertion
 *
 * @example
 * // Returns "&lt;script&gt;alert('XSS')&lt;/script&gt;"
 * escapeHtml("<script>alert('XSS')</script>")
 *
 * @example
 * // Returns "Test &amp; Demo"
 * escapeHtml("Test & Demo")
 */
export const escapeHtml = (str) => {
  if (str === null || str === undefined) {
    return '';
  }

  const stringValue = String(str);

  const htmlEscapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };

  return stringValue.replace(/[&<>"'`=/]/g, (char) => htmlEscapeMap[char]);
};

// Extract scanErrorMessage from a scan run summary for failed runs.
// Returns the error string if present, otherwise null.
export const getScanErrorMessage = (summary) => {
  if (!summary) return null;

  try {
    const parsed = typeof summary === 'string' ? JSON.parse(summary) : summary;
    const message = parsed?.scanMetrics?.scanErrorMessage;

    return typeof message === 'string' && message.trim() ? message : null;
  } catch (e) {
    return null;
  }
};

// Format scan summary object to readable string
export const formatScanSummary = (summary) => {
  if (!summary) return '-';

  try {
    // Parse JSON if it's a string
    const parsed = typeof summary === 'string' ? JSON.parse(summary) : summary;

    if (!parsed || typeof parsed !== 'object') {
      return summary || '-';
    }

    // Use stats if it exists, otherwise use the parsed object directly
    const stats = parsed.stats || parsed;

    if (!stats || typeof stats !== 'object') {
      return '-';
    }

    const parts = [];

    if (stats.added !== undefined) {
      parts.push(`${stats.added} Added`);
    }
    if (stats.updated !== undefined) {
      parts.push(`${stats.updated} Updated`);
    }
    if (stats.removed !== undefined) {
      parts.push(`${stats.removed} Removed`);
    }
    if (stats.unchanged !== undefined) {
      parts.push(`${stats.unchanged} Already in Exchange`);
    }
    const errorsList = stats.errors ?? 0;
    const pendings = stats.pending ?? 0;

    // Display when errors or pending items exist.
    if (errorsList + pendings > 0) {
      parts.push(`${errorsList + pendings} Unsuccessful`);
    }

    return parts.length > 0 ? parts.join(', ') : '-';
  } catch (e) {
    // If parsing fails, return original or default
    return summary || '-';
  }
};

// Get status class based on status value
export const getStatusClass = (status, styles) => {
  const normalizedStatus = status?.toUpperCase();

  switch (normalizedStatus) {
    case 'CLOSE':
    case 'ERROR':
    case 'ABORTED':
    case 'FAILED':
    case 'STOPPED':
      return styles.statusError;
    case 'PARTIALSUCCESS':
      return styles.statusWarning;
    case 'PENDING_REVIEW':
      return styles.statusPendingReview;
    case 'STALE':
      return styles.statusStale;
    case 'IDLE':
      return styles.statusIdle;
    case 'RUNNING':
    case 'IMPORTING':
      return styles.statusActive;
    default: // SUCCESS / COMPLETED / SCHEDULED
      return styles.statusFinished;
  }
};

export const handleBack = (history) => {
  // Go back to the agentScanners list by removing the scanner ID from the path
  const currentPath = history.location.pathname;
  // Remove trailing slash if present, then remove the last segment (scanner ID)
  const pathWithoutTrailingSlash = currentPath.replace(/\/$/, '');
  const basePath = pathWithoutTrailingSlash.replace(/\/[^/]+$/, '');
  // Ensure the base path ends with / for consistency
  const finalPath = basePath.endsWith('/') ? basePath : `${basePath}/`;

  history.push(finalPath);
};

export const parseParameters = (parameters) => {
  try {
    if (!parameters) return null;
    // Backend can send: object directly, JSON string, or double-serialized JSON string
    if (typeof parameters === 'object') return parameters;
    const parsed =
      typeof parameters === 'string' ? JSON.parse(parameters) : parameters;

    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch (e) {
    return null;
  }
};

/**
 * Format hour to 12-hour format with am/pm
 * @param {number} hour - Hour in 24-hour format (0-23)
 * @returns {string} Formatted time (e.g., "2am", "2pm", "12pm")
 */
const formatHour = (hour) => {
  if (
    typeof hour !== 'number' ||
    hour < 0 ||
    hour > 23 ||
    !Number.isInteger(hour)
  ) {
    throw new Error('Hour must be an integer between 0 and 23');
  }

  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;

  return `${hour - 12}pm`;
};

/**
 * Convert UTC hour (from backend) to browser local hour for display.
 * Backend always returns UTC; UI shows browser local time.
 * @param {string|number} utcHour - Hour in UTC from backend (0-23)
 * @returns {number} Hour in browser local time (0-23)
 */
export const convertUTCHourToLocal = (utcHour) => {
  const hour = typeof utcHour === 'string' ? parseInt(utcHour, 10) : utcHour;
  const offsetMinutes = new Date().getTimezoneOffset();
  const localHour = (Math.round(hour - offsetMinutes / 60) + 24) % 24;

  return localHour;
};

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

/**
 * Get day name from ISO-8601 day number (1-7, Monday-Sunday)
 * @param {number} dayNumber - Day number (1=Monday, 7=Sunday)
 * @returns {string} Day name
 */
const getDayName = (dayNumber) => {
  if (
    typeof dayNumber !== 'number' ||
    dayNumber < 1 ||
    dayNumber > 7 ||
    !Number.isInteger(dayNumber)
  ) {
    throw new Error('Day number must be an integer between 1 and 7');
  }

  return DAYS[dayNumber - 1];
};

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
 * @param {number} n - Number
 * @returns {string} Number with ordinal suffix
 */
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;

  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const authMapper = async (value, context, organizationId) => {
  if (!value) return value;

  const valueKey = value.toLowerCase();

  // Build map dynamically from getAgentProviders API response
  const authMethodMap = {};
  const { providers = [] } = await getAgentProviders(context, organizationId);

  providers.forEach((provider) => {
    provider.authSchemeFormFields?.forEach(({ name, authScheme }) => {
      const lowerKey = name.toLowerCase();

      // Map normalized variations of the display name
      authMethodMap[lowerKey] = name;
      authMethodMap[lowerKey.replace(/\s+/g, '_')] = name;
      authMethodMap[lowerKey.replace(/\s+/g, '-')] = name;

      // Map authScheme value to the display name
      if (authScheme) {
        authMethodMap[authScheme.toLowerCase()] = name;
      }
    });
  });

  return authMethodMap[valueKey] || value;
};

/**
 * Synchronous auth scheme label. Returns the raw value as-is; callers that
 * need a human-readable label should use the async authMapper.
 * @param {string} value - Auth scheme value
 * @returns {string} Same value
 */
export const authMapperSync = (value) => value;

/**
 * Parse a JSON schedule object to readable string
 *
 * JSON Schedule Format:
 * - unit (required): "day" | "week" | "month"
 * - interval (optional, default 1): How many units between executions
 * - at (required): Hour of day (0-23)
 * - on (optional): Day of week (1-7, ISO-8601) for week, or day of month (1-31) for month
 *
 * @param {string|Object} schedule - JSON string or object with schedule info
 * @returns {string} Human readable schedule description
 *
 * @example
 * // Returns "Daily at 2am"
 * parseScheduleJson({ unit: "day", at: 2 })
 *
 * @example
 * // Returns "Weekly on Thursday at 2am"
 * parseScheduleJson({ unit: "week", on: 4, at: 2 })
 *
 * @example
 * // Returns "Monthly on the 18th at 2am"
 * parseScheduleJson({ unit: "month", on: 18, at: 2 })
 *
 * @example
 * // Returns "Every 3 days at 2am"
 * parseScheduleJson({ unit: "day", interval: 3, at: 2 })
 *
 * @example
 * // Returns "Every 3 weeks on Monday at 2am"
 * parseScheduleJson({ unit: "week", interval: 3, on: 1, at: 2 })
 */
export const parseScheduleJson = (schedule) => {
  if (!schedule) return 'Not scheduled';

  try {
    const parsed =
      typeof schedule === 'string' ? JSON.parse(schedule) : schedule;

    const { unit, at, on } = parsed;
    // Convert to numbers since backend may send strings
    const interval = Number(parsed.interval) || 1;
    const atNum = Number(at);
    const onNum = on !== undefined ? Number(on) : undefined;

    // Validate required fields
    if (
      !unit ||
      at === undefined ||
      at === null ||
      at === '' ||
      Number.isNaN(atNum)
    ) {
      return 'Not scheduled';
    }

    const localHour = convertUTCHourToLocal(atNum);
    const timeStr = formatHour(localHour);
    const isInterval = interval > 1;

    switch (unit) {
      case 'day':
        if (isInterval) {
          return `Every ${interval} days at ${timeStr}`;
        }

        return `Daily at ${timeStr}`;

      case 'week':
        if (isInterval) {
          // Every N weeks, optionally on a specific day
          if (onNum) {
            return `Every ${interval} weeks on ${getDayName(onNum)} at ${timeStr}`;
          }

          return `Every ${interval} weeks at ${timeStr}`;
        }
        // Weekly on specific day - 'on' is required for simple weekly schedules
        if (!onNum) {
          return 'Not scheduled';
        }

        return `Weekly on ${getDayName(onNum)} at ${timeStr}`;

      case 'month':
        // Monthly on specific day of month - 'on' is required
        if (!onNum) {
          return 'Not scheduled';
        }

        return `Monthly on the ${getOrdinal(onNum)} at ${timeStr}`;

      default:
        return 'Not scheduled';
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Error parsing schedule:', e);

    return 'Not scheduled';
  }
};

/**
 * Parse schedule from JSON format
 * @param {string|Object} schedule - Schedule in JSON format
 * @returns {string} Human readable schedule description
 */
export const parseSchedule = (schedule) => {
  if (!schedule) {
    return 'Not scheduled';
  }

  // If it's already an object, parse directly
  if (typeof schedule === 'object' && schedule !== null) {
    return parseScheduleJson(schedule);
  }

  // Try to parse JSON string
  if (typeof schedule === 'string') {
    const trimmed = schedule.trim();

    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return parseScheduleJson(trimmed);
    }
  }

  return 'Not scheduled';
};

/**
 * Format status text with first letter uppercase and rest lowercase
 * @param {string} status - Status string (e.g., 'ACTIVE', 'running')
 * @param {string} fallback - Fallback value if status is falsy (default: '-')
 * @returns {string} Formatted status (e.g., 'Active', 'Running') or fallback
 */
export const formatStatusText = (status, fallback = '-') => {
  if (!status) return fallback;
  const statusInfo = getScannerStatusByKey(status);

  if (statusInfo === ScannerStatusEnum.UNKNOWN)
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

  return statusInfo.label;
};

export const formatRunStatusText = (runStatus, fallback = '-') => {
  if (!runStatus) return fallback;
  const runStatusInfo = getRunStatusByKey(runStatus);

  if (runStatusInfo === RunStatusEnum.UNKNOWN)
    return runStatus.charAt(0).toUpperCase() + runStatus.slice(1).toLowerCase();

  return runStatusInfo.label;
};

export const formatRelativeTime = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now; // Positive if future, negative if past
    const absDiffMs = Math.abs(diffMs);
    const absDiffSeconds = Math.floor(absDiffMs / 1000);
    const absDiffMinutes = Math.floor(absDiffSeconds / 60);
    const absDiffHours = Math.floor(absDiffMinutes / 60);
    const absDiffDays = Math.floor(absDiffHours / 24);
    const isFuture = diffMs > 0;

    // Handle very recent times (within 60 seconds)
    if (absDiffSeconds < 60) {
      return isFuture ? 'In a moment' : 'Just now';
    }

    // Handle minutes
    if (absDiffMinutes < 60) {
      return isFuture
        ? `In ${absDiffMinutes} min${absDiffMinutes > 1 ? 's' : ''}`
        : `${absDiffMinutes} min${absDiffMinutes > 1 ? 's' : ''} ago`;
    }

    // Handle hours
    if (absDiffHours < 24) {
      return isFuture
        ? `In ${absDiffHours} hr${absDiffHours > 1 ? 's' : ''}`
        : `${absDiffHours} hr${absDiffHours > 1 ? 's' : ''} ago`;
    }

    // Handle days (up to 7 days)
    if (absDiffDays < 7) {
      return isFuture
        ? `In ${absDiffDays} day${absDiffDays > 1 ? 's' : ''}`
        : `${absDiffDays} day${absDiffDays > 1 ? 's' : ''} ago`;
    }

    // For dates beyond 7 days, return the date string
    return date.toDateString();
  } catch (e) {
    return dateString;
  }
};

// Helper function to get stats from lastSuccessfulRunSummary (handles JSON string or object)
export const getStatsFromSummary = (summary) => {
  if (!summary) return null;

  try {
    // Parse JSON if it's a string
    const parsed = typeof summary === 'string' ? JSON.parse(summary) : summary;

    // Use stats if it exists, otherwise use the parsed object directly
    return parsed?.stats || parsed;
  } catch (e) {
    console.error('Failed to parse lastSuccessfulRunSummary:', e);

    return null;
  }
};

export const getStagingAgentsByType = (
  agents,
  type,
  field = 'operationPerformed'
) => {
  return agents ? agents.filter((item) => item[field] === type) : [];
};
export const getDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) {
    return '';
  }

  const startDate = new Date(startedAt);
  const endDate = new Date(endedAt);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '';
  }

  let delta = Math.abs(endDate - startDate) / 1000; // Total seconds

  const hours = Math.floor(delta / 3600) % 24; // Seconds in an hour

  delta -= hours * 3600;

  const minutes = Math.floor(delta / 60) % 60; // Seconds in a minute

  delta -= minutes * 60;

  const seconds = Math.floor(delta % 60); // Remaining seconds

  return `${hours > 0 ? `${hours}hs ` : ''}${minutes}m ${seconds}s`;
};

/**
 * @param {Object} scanner - Scanner object from the API
 * @returns {string} Human-readable asset type label or '-'
 */
export const getAssetTypeLabel = (scanner) => {
  const assetType = scanner?.connection?.targetSystem?.assetType;

  if (!assetType) return '-';

  return ASSET_TYPE_LABELS[assetType?.toLowerCase()] || assetType;
};

export const isScanInProgress = (status) => {
  const inProgressStatuses = [
    ScannerStatusEnum.QUEUED.key,
    ScannerStatusEnum.RUNNING.key,
    ScannerStatusEnum.IMPORTING.key
  ];

  return inProgressStatuses.includes(status.toLowerCase());
};

/**
 * Check if a scan cannot be aborted based on its status
 * A scan cannot be aborted if it's not currently in progress
 * @param {string} status - Scanner status
 * @returns {boolean} True if scan cannot be aborted, false otherwise
 */
export const cannotAbortScan = (status) => {
  return !isScanInProgress(status);
};
